
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { complianceService } from '../../services/api';

interface NewRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

interface DSARFormData {
    userEmail: string;
    type: 'ACCESS' | 'DELETION' | 'RECTIFICATION';
    description: string;
    receivedDate: string;
}

const NewRequestModal: React.FC<NewRequestModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<DSARFormData>();
    const [error, setError] = useState<string | null>(null);

    const onSubmit = async (data: DSARFormData) => {
        setError(null);
        try {
            // Mocking the proper payload structure expected by backend
            await complianceService.submitDSAR({
                ...data,
                status: 'PENDING',
                priority: 'MEDIUM'
            });
            reset();
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Failed to submit DSAR", err);
            setError("Failed to log request. Please try again.");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} aria-hidden="true"></div>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div>
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                            <i className="fas fa-user-shield text-blue-600"></i>
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                Log Data Subject Request
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    Record a new request from a tenant regarding their personal data.
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)} className="mt-5 sm:mt-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Tenant Email</label>
                            <input
                                type="email"
                                {...register('userEmail', { required: 'Email is required' })}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="tenant@example.com"
                            />
                            {errors.userEmail && <p className="text-red-500 text-xs mt-1">{errors.userEmail.message}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Request Type</label>
                            <select
                                {...register('type', { required: true })}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            >
                                <option value="ACCESS">Access Request (SAR)</option>
                                <option value="DELETION">Deletion (Right to be Forgotten)</option>
                                <option value="RECTIFICATION">Correction of Data</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Date Received</label>
                            <input
                                type="date"
                                {...register('receivedDate', { required: 'Date is required' })}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            {errors.receivedDate && <p className="text-red-500 text-xs mt-1">{errors.receivedDate.message}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Description / Notes</label>
                            <textarea
                                rows={3}
                                {...register('description')}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="Details of specific data requested..."
                            />
                        </div>

                        {error && <div className="text-red-500 text-sm text-center bg-red-50 p-2 rounded">{error}</div>}

                        <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                            >
                                {isSubmitting ? 'Logging...' : 'Log Request'}
                            </button>
                            <button
                                type="button"
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default NewRequestModal;
